import { useState, useCallback } from 'react';
import { useActiveTemplate } from './useActiveTemplate';
import { elementRegistry } from '../../elements';
import { useTemplateStore } from '../store/templateStore';
import useUiStore from '../store/uiStore';
import { logger } from '../../core/logging';
import { AppError } from '../../core/errors';
import { WordApiError } from '../../core/errors/WordApiError';

/**
 * Hook that centralises element insertion. This is the ONLY place in the
 * React layer that may call `element.insertAsync()`.
 */
export function useInsertElement(): {
  insert: (elementId: string) => Promise<void>;
  isInserting: boolean;
  insertingId: string | null;
} {
  const { profile, syncFromDocument } = useActiveTemplate();
  const showToast = useUiStore((s) => s.showToast);

  const [isInserting, setIsInserting] = useState(false);
  const [insertingId, setInsertingId] = useState<string | null>(null);

  const insert = useCallback(async (elementId: string) => {
    if (isInserting) {
      logger.debug('Insert ignored — an insert is already running', { elementId });
      return;
    }
    setIsInserting(true);
    setInsertingId(elementId);
    const startedAt = Date.now();
    try {
      logger.info('Insert requested', { elementId });

      await syncFromDocument();
      const currentProfile = useTemplateStore.getState().activeProfile;

      const element = elementRegistry.getById(elementId);
      logger.debug('Element resolved', { elementId, profileId: currentProfile.id });

      await element.insertAsync(currentProfile);

      logger.info('Insert succeeded', {
        elementId,
        profileId: currentProfile.id,
        durationMs: Date.now() - startedAt,
      });
      showToast({ level: 'success', message: `${element.label} inserted` });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      // Show friendly toast for known app or Word API errors
      if (AppError.isAppError(err) || err instanceof WordApiError) {
        showToast({ level: 'error', message });
      } else {
        showToast({ level: 'error', message: 'An unexpected error occurred' });
      }
      logger.error('Insert failed', {
        elementId,
        durationMs: Date.now() - startedAt,
        error: err,
      });
    } finally {
      setIsInserting(false);
      setInsertingId(null);
    }
  }, [isInserting, showToast, syncFromDocument]);

  return { insert, isInserting, insertingId };
}
